/**
 * LA SUBIDA MIENTRAS SE GRABA (spec §4.12.3): multipart prefirmado a R2, partes iguales de
 * exactamente 8 MiB (la última libre), PUT por `XMLHttpRequest` (progreso real), 2 en vuelo,
 * reintento ×5 con backoff, pausa en `offline`. Nada pasa por el backend: el navegador pide URLs
 * por lotes (`POST /:id/parts`, hasta 10) y sube directo al bucket.
 *
 * RECUPERACIÓN: lo que se guarda es `contiguousDone` = el mayor n tal que las partes 1..n tienen ETag,
 * NO el conteo de partes confirmadas. Con dos en vuelo la parte 2 puede confirmarse antes que la 1;
 * si la pestaña se cierra ahí, "1 parte" era mentira (R2 tiene [2]) y `complete` armaba un objeto
 * con hueco. El backend además exige contiguidad (400 `parts_missing`).
 *
 * PRESIGN: `POST /:id/parts` no se reintenta solo en `api.ts` (es un POST) y un 502 de un deploy
 * consumía uno de los 5 intentos de la parte: cinco 502 seguidos (~23 s) y la cola quedaba en
 * `failed` a mitad de la grabación. Acá la firma se reintenta con backoff propio ante 502/503/504/429
 * y caída de red; solo un 4xx de verdad (403/404/409) falla la parte.
 *
 * Lo que se puede probar sin navegador está separado en funciones puras:
 *  - `PartCutter`: el corte EXACTO en 8 MiB (`new Blob(chunks).slice(0, 8 MiB)`).
 *  - `shouldAutoPause` / `pendingSeconds`: la auto-PAUSA por TIEMPO de media pendiente (> 75 s
 *    pausa, < 15 s reanuda — con histéresis, para no oscilar), no por bytes.
 *  - `estimateRemainingSec` / `fmtRemaining`: "≈ {tiempo} restante" = pendiente / velocidad medida.
 *  - `parseRecovery`: el estado mínimo guardado para "Unfinished recording — Recover / Discard".
 *
 * Los PUT van SIN `Content-Type`: la URL se firma solo con `host` (spec: si el XHR manda el
 * `Blob.type` del chunk y la firma no lo incluye, R2 contesta 403). Por eso todo Blob que sale por
 * el cable se re-envuelve sin tipo.
 */
import type { PartUrl } from './types';

export const PART_SIZE = 8 * 1024 * 1024; // 8388608, el mismo `partSize` que devuelve POST /api/recordings
export const IN_FLIGHT = 2;
export const MAX_ATTEMPTS = 5;
export const PRESIGN_BATCH = 10;
/** Renovar la URL de una parte si le quedan menos de esto (las firmas valen 900 s). */
const URL_MIN_LEFT_MS = 60_000;
export const AUTO_PAUSE_ABOVE_SEC = 75;
export const AUTO_RESUME_BELOW_SEC = 15;
const SPEED_WINDOW_MS = 15_000;
const PUT_TIMEOUT_MS = 180_000;
/** Backoff entre reintentos de UNA parte (ms). */
export const RETRY_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000, 8_000];
/** Backoff de la FIRMA (presign) mientras el backend arranca (502/503/504) o no hay red: no gasta intentos de la parte. */
export const PRESIGN_RETRY_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000, 15_000, 15_000];
const PRESIGN_RETRY_STATUS = new Set([429, 502, 503, 504]);

/** ¿El fallo de la firma es "espera y vuelve" (deploy, red) o definitivo (403/404/409)? */
export function presignRetryable(e: unknown): boolean {
  const st = (e as { status?: unknown } | null)?.status;
  const code = (e as { code?: unknown } | null)?.code;
  if (code === 'network_error') return true;
  if (typeof st === 'number') return PRESIGN_RETRY_STATUS.has(st) || st === 0;
  // Sin status: un TypeError de fetch (red caída) — se espera.
  return true;
}

// ───────────────────────── corte exacto ─────────────────────────

export class PartCutter {
  private chunks: Blob[] = [];
  private size = 0;
  constructor(private readonly partSize: number = PART_SIZE) {}
  /** Bytes que todavía no completan una parte. */
  get pendingBytes(): number { return this.size; }
  /** Suma un chunk del grabador; devuelve las partes COMPLETAS (exactamente `partSize`) que salieron. */
  push(b: Blob): Blob[] {
    if (!b || !b.size) return [];
    this.chunks.push(b);
    this.size += b.size;
    const out: Blob[] = [];
    while (this.size >= this.partSize) {
      const all = new Blob(this.chunks);
      out.push(all.slice(0, this.partSize));
      const rest = all.slice(this.partSize);
      this.chunks = rest.size ? [rest] : [];
      this.size = rest.size;
    }
    return out;
  }
  /** La última parte (lo que quede, puede ser menor que `partSize`); null si no hay nada. */
  flush(): Blob | null {
    if (!this.size) return null;
    const b = new Blob(this.chunks);
    this.chunks = [];
    this.size = 0;
    return b;
  }
}

// ───────────────────────── aritmética pura ─────────────────────────

/**
 * ¿Hay que pausar la grabación porque la subida no alcanza? Histéresis: sin pausa se pausa al pasar
 * de `above` segundos de media pendiente; en pausa se sigue en pausa hasta bajar de `below`.
 */
export function shouldAutoPause(pendingSec: number, pausedByNet: boolean, th: { above?: number; below?: number } = {}): boolean {
  const above = th.above ?? AUTO_PAUSE_ABOVE_SEC;
  const below = th.below ?? AUTO_RESUME_BELOW_SEC;
  if (!Number.isFinite(pendingSec)) return pausedByNet;
  return pausedByNet ? pendingSec >= below : pendingSec > above;
}

/**
 * Segundos de GRABACIÓN que representan los bytes pendientes, a la tasa media medida
 * (bytes grabados / tiempo grabado). Con menos de 2 s grabados no hay tasa confiable → 0.
 */
export function pendingSeconds(pendingBytes: number, recordedBytes: number, recordedMs: number): number {
  if (pendingBytes <= 0 || recordedBytes <= 0 || recordedMs < 2_000) return 0;
  const bytesPerSec = recordedBytes / (recordedMs / 1000);
  return bytesPerSec > 0 ? pendingBytes / bytesPerSec : 0;
}

/** Segundos que faltan de subida a la velocidad medida; null sin velocidad (todavía no se midió). */
export function estimateRemainingSec(pendingBytes: number, bytesPerSec: number): number | null {
  if (pendingBytes <= 0) return 0;
  if (!Number.isFinite(bytesPerSec) || bytesPerSec <= 0) return null;
  return Math.ceil(pendingBytes / bytesPerSec);
}

/** "40s" · "3 min" · "1 h 5 min" — neutro entre EN y ES (la frase alrededor la pone t()). */
export function fmtRemaining(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h} h ${mm} min` : `${h} h`;
}

/** Velocidad de subida medida por los `onprogress` del XHR en una ventana deslizante. */
export class SpeedMeter {
  private samples: Array<{ t: number; bytes: number }> = [];
  constructor(private readonly windowMs: number = SPEED_WINDOW_MS) {}
  add(t: number, deltaBytes: number): void {
    if (deltaBytes <= 0) return;
    this.samples.push({ t, bytes: deltaBytes });
    this.trim(t);
  }
  bytesPerSec(now: number): number {
    this.trim(now);
    if (this.samples.length < 2) return 0;
    const first = this.samples[0]!.t;
    const span = Math.max(1, now - first);
    const total = this.samples.reduce((a, s) => a + s.bytes, 0);
    return total / (span / 1000);
  }
  private trim(now: number): void {
    const min = now - this.windowMs;
    while (this.samples.length && this.samples[0]!.t < min) this.samples.shift();
  }
}

// ───────────────────────── recuperación ─────────────────────────

export type Recovery = {
  id: number;
  /** Partes con ETag confirmado en R2 (las que `complete` va a contar). */
  partsUploaded: number;
  startedAt: number;
  recordedMs: number;
  mime: string;
  title: string | null;
};

export const RECOVERY_KEY = 'ppag-rec-unfinished';

export function parseRecovery(raw: string | null | undefined): Recovery | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as Partial<Recovery> | null;
    if (!o || typeof o !== 'object') return null;
    const id = Number(o.id);
    const parts = Number(o.partsUploaded);
    if (!Number.isInteger(id) || id <= 0) return null;
    if (!Number.isInteger(parts) || parts < 0) return null;
    return {
      id,
      partsUploaded: parts,
      startedAt: Number.isFinite(Number(o.startedAt)) ? Number(o.startedAt) : 0,
      recordedMs: Number.isFinite(Number(o.recordedMs)) ? Math.max(0, Number(o.recordedMs)) : 0,
      mime: typeof o.mime === 'string' ? o.mime : '',
      title: typeof o.title === 'string' && o.title ? o.title : null,
    };
  } catch { return null; }
}

/**
 * Vive en `localStorage` (no en sessionStorage): la recuperación existe justamente para cuando se
 * CIERRA la pestaña a mitad, y sessionStorage muere con ella.
 */
export function loadRecovery(): Recovery | null {
  try { return parseRecovery(localStorage.getItem(RECOVERY_KEY)); } catch { return null; }
}
export function saveRecovery(r: Recovery): void {
  try { localStorage.setItem(RECOVERY_KEY, JSON.stringify(r)); } catch { /* sin storage: sin recuperación */ }
}
export function clearRecovery(id?: number): void {
  try {
    if (id != null) { const cur = loadRecovery(); if (cur && cur.id !== id) return; }
    localStorage.removeItem(RECOVERY_KEY);
  } catch { /* */ }
}

// ───────────────────────── el PUT ─────────────────────────

/** PUT por XHR con progreso. El Blob sale SIN tipo para que no viaje `Content-Type` (la firma no lo incluye). */
export function xhrPut(url: string, blob: Blob, onProgress: (loaded: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.timeout = PUT_TIMEOUT_MS;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable || e.loaded) onProgress(e.loaded); };
    xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(); else reject(new Error(`http_${xhr.status}`)); };
    xhr.onerror = () => reject(new Error('network'));
    xhr.ontimeout = () => reject(new Error('timeout'));
    xhr.onabort = () => reject(new Error('aborted'));
    xhr.send(blob.type ? new Blob([blob]) : blob);
  });
}

// ───────────────────────── la cola ─────────────────────────

export type UploaderState = {
  /** Partes cortadas (completas + la última tras `finish`). */
  partsQueued: number;
  partsDone: number;
  /** Mayor n tal que las partes 1..n tienen ETag (lo que `complete` puede contar sin hueco). */
  contiguousDone: number;
  /** Bytes grabados que todavía no tienen ETag: en el cortador, en cola o en vuelo. */
  pendingBytes: number;
  /** Bytes que entraron al uploader en total. */
  totalBytes: number;
  bytesPerSec: number;
  offline: boolean;
  /** Se agotaron los reintentos de alguna parte: la cola queda quieta hasta `retry()`. */
  failed: boolean;
  error: string | null;
  /** `finish()` ya se llamó y todo subió. */
  done: boolean;
};

export type UploaderDeps = {
  presign(from: number, count: number): Promise<PartUrl[]>;
  put?: (url: string, blob: Blob, onProgress: (loaded: number) => void) => Promise<void>;
  onState(s: UploaderState): void;
  /** Cada parte confirmada, con las partes CONTIGUAS confirmadas (para guardar la recuperación). */
  onPartDone?: (contiguousDone: number) => void;
  now?: () => number;
  isOnline?: () => boolean;
  sleep?: (ms: number) => Promise<void>;
  partSize?: number;
  inFlight?: number;
  retryMs?: readonly number[];
  presignRetryMs?: readonly number[];
};

type Part = { n: number; blob: Blob; attempts: number; loaded: number };

export class Uploader {
  private readonly cutter: PartCutter;
  private readonly queue: Part[] = [];
  private readonly urls = new Map<number, { url: string; expiresAt: number }>();
  private presignInFlight: Promise<void> | null = null;
  private readonly confirmed = new Set<number>();
  private inFlight = 0;
  private finished = false;
  private aborted = false;
  private readonly meter = new SpeedMeter();
  private settle: { resolve: (n: number) => void; reject: (e: Error) => void } | null = null;
  private readonly put: NonNullable<UploaderDeps['put']>;
  private readonly now: () => number;
  private readonly isOnline: () => boolean;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly maxInFlight: number;
  private readonly retryMs: readonly number[];
  private readonly presignRetryMs: readonly number[];
  readonly state: UploaderState = {
    partsQueued: 0, partsDone: 0, contiguousDone: 0, pendingBytes: 0, totalBytes: 0, bytesPerSec: 0, offline: false, failed: false, error: null, done: false,
  };

  constructor(private readonly deps: UploaderDeps) {
    this.cutter = new PartCutter(deps.partSize ?? PART_SIZE);
    this.put = deps.put ?? xhrPut;
    this.now = deps.now ?? (() => Date.now());
    this.isOnline = deps.isOnline ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
    this.sleep = deps.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
    this.maxInFlight = deps.inFlight ?? IN_FLIGHT;
    this.retryMs = deps.retryMs ?? RETRY_MS;
    this.presignRetryMs = deps.presignRetryMs ?? PRESIGN_RETRY_MS;
  }

  /** Un chunk del grabador (`dataavailable`). */
  push(blob: Blob): void {
    if (this.finished || this.aborted || !blob.size) return;
    this.state.totalBytes += blob.size;
    this.state.pendingBytes += blob.size;
    for (const b of this.cutter.push(blob)) this.enqueue(b);
    this.emit();
    this.pump();
  }

  /** Stop: corta la última parte y espera a que TODO tenga ETag. Resuelve con `partsSent`. */
  finish(): Promise<number> {
    if (this.settle) return Promise.reject(new Error('already_finishing'));
    this.finished = true;
    const last = this.cutter.flush();
    if (last) this.enqueue(last);
    this.emit();
    return new Promise<number>((resolve, reject) => {
      this.settle = { resolve, reject };
      this.pump();
      this.check();
    });
  }

  /** Tras `failed`: vuelve a intentar lo que quedó en cola. */
  retry(): void {
    if (!this.state.failed) return;
    this.state.failed = false;
    this.state.error = null;
    for (const p of this.queue) p.attempts = 0;
    this.emit();
    this.pump();
  }

  abort(): void {
    this.aborted = true;
    this.queue.length = 0;
    this.settle?.reject(new Error('aborted'));
    this.settle = null;
  }

  /** ¿Quedan bytes sin confirmar? (para el aviso de `beforeunload`). */
  get pending(): boolean { return this.state.pendingBytes > 0 && !this.state.done; }

  private enqueue(blob: Blob): void {
    this.state.partsQueued += 1;
    this.queue.push({ n: this.state.partsQueued, blob, attempts: 0, loaded: 0 });
  }

  private emit(): void {
    this.state.bytesPerSec = this.meter.bytesPerSec(this.now());
    this.deps.onState({ ...this.state });
  }

  private check(): void {
    if (!this.settle) return;
    if (this.state.failed) return; // se queda esperando `retry()`
    if (this.queue.length === 0 && this.inFlight === 0) {
      this.state.done = true;
      this.emit();
      const s = this.settle;
      this.settle = null;
      s.resolve(this.state.partsQueued);
    }
  }

  private pump(): void {
    if (this.aborted || this.state.failed) return;
    while (this.inFlight < this.maxInFlight && this.queue.length) {
      const part = this.queue.shift()!;
      this.inFlight += 1;
      void this.run(part);
    }
  }

  private async waitOnline(): Promise<void> {
    if (this.isOnline()) { if (this.state.offline) { this.state.offline = false; this.emit(); } return; }
    if (!this.state.offline) { this.state.offline = true; this.emit(); }
    while (!this.isOnline() && !this.aborted) await this.sleep(1_000);
    this.state.offline = false;
    this.emit();
  }

  private async ensureUrl(n: number): Promise<string> {
    const cached = this.urls.get(n);
    if (cached && cached.expiresAt - this.now() > URL_MIN_LEFT_MS) return cached.url;
    if (this.presignInFlight) {
      await this.presignInFlight;
      const again = this.urls.get(n);
      if (again && again.expiresAt - this.now() > URL_MIN_LEFT_MS) return again.url;
    }
    // Un lote desde `n`: cubre las partes que vienen detrás sin un POST por cada una. Un 502/503/504
    // (deploy) o la red caída se ESPERAN con backoff propio: no cuentan como intento de la parte.
    this.presignInFlight = (async () => {
      for (let intento = 0; ; intento += 1) {
        try {
          const rows = await this.deps.presign(n, PRESIGN_BATCH);
          for (const r of rows) {
            const exp = Date.parse(r.expiresAt);
            this.urls.set(r.partNumber, { url: r.url, expiresAt: Number.isFinite(exp) ? exp : this.now() + 600_000 });
          }
          return;
        } catch (e) {
          if (this.aborted || !presignRetryable(e) || intento >= this.presignRetryMs.length) throw e;
          await this.sleep(this.presignRetryMs[intento] ?? 15_000);
          await this.waitOnline();
        }
      }
    })();
    try { await this.presignInFlight; } finally { this.presignInFlight = null; }
    const got = this.urls.get(n);
    if (!got) throw new Error('presign_missing_part');
    return got.url;
  }

  private async run(part: Part): Promise<void> {
    try {
      for (;;) {
        if (this.aborted) return;
        await this.waitOnline();
        try {
          const url = await this.ensureUrl(part.n);
          let lastT = this.now();
          part.loaded = 0;
          await this.put(url, part.blob, (loaded) => {
            const t = this.now();
            const delta = loaded - part.loaded;
            part.loaded = loaded;
            if (delta > 0) this.meter.add(t, delta);
            if (t - lastT > 500) { lastT = t; this.emit(); }
          });
          this.state.partsDone += 1;
          this.confirmed.add(part.n);
          while (this.confirmed.has(this.state.contiguousDone + 1)) this.state.contiguousDone += 1;
          this.state.pendingBytes = Math.max(0, this.state.pendingBytes - part.blob.size);
          this.deps.onPartDone?.(this.state.contiguousDone);
          this.emit();
          return;
        } catch (e) {
          if (this.aborted) return;
          part.attempts += 1;
          part.loaded = 0;
          // Una URL vencida o rota se renueva en el próximo intento.
          this.urls.delete(part.n);
          if (part.attempts >= MAX_ATTEMPTS) {
            // Se devuelve al frente de la cola para `retry()` y se para todo.
            this.queue.unshift(part);
            this.state.failed = true;
            this.state.error = e instanceof Error ? e.message : String(e);
            this.emit();
            return;
          }
          await this.sleep(this.retryMs[Math.min(part.attempts - 1, this.retryMs.length - 1)] ?? 8_000);
        }
      }
    } finally {
      this.inFlight -= 1;
      this.pump();
      this.check();
    }
  }
}
